import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Clock, TrendingUp, TrendingDown, Users, Coins, ExternalLink, Copy, Check, Twitter, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useLocale } from "@/contexts/LocaleContext";
import { supabase } from "@/integrations/supabase/client";
import { useRateLimit, RATE_LIMIT_CONFIGS } from "@/hooks/useRateLimit";
import { getTimeRemaining, isDeadlinePassed as checkDeadlinePassed, getEventStatusDisplay } from "@/lib/date-utils";

interface EventDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: {
    id: string;
    title: string;
    description: string;
    yes_pool: number;
    no_pool: number;
    total_bids: number;
    deadline: string;
    status: "OPEN" | "CLOSED" | "RESOLVED";
    outcome?: boolean | null;
    language?: string;
  } | null;
  onBidPlaced: () => void;
  prefetchedUserBid?: { amount: number; prediction: boolean } | null;
}

const languageFlags: Record<string, string> = {
  en: "🇺🇸",
  pt: "🇧🇷",
  es: "🇪🇸",
  fr: "🇫🇷",
  de: "🇩🇪",
  it: "🇮🇹",
  global: "🌍",
};

const EventDetailModal = ({ isOpen, onClose, event, onBidPlaced, prefetchedUserBid }: EventDetailModalProps) => {
  const { user, profile, refetchProfile } = useAuth();
  const { locale, formatCurrency, t } = useLocale();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [amount, setAmount] = useState("");
  const [prediction, setPrediction] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [userBid, setUserBid] = useState<{ amount: number; prediction: boolean } | null>(null);
  const [loadingBid, setLoadingBid] = useState(false);
  const { isLimited, checkRateLimit, recordAction } = useRateLimit(RATE_LIMIT_CONFIGS.bidPlacement);

  const handleLoginRedirect = () => {
    const returnTo = event?.id ? `/event/${event.id}` : "/";
    onClose();
    navigate(`/auth?returnTo=${encodeURIComponent(returnTo)}`, { state: { from: returnTo } });
  };

  // Use prefetched data if available, otherwise fetch
  useEffect(() => {
    const fetchUserBid = async () => {
      if (!user || !event || !isOpen) {
        setUserBid(null);
        return;
      }
      
      // Use prefetched data if available
      if (prefetchedUserBid !== undefined) {
        setUserBid(prefetchedUserBid);
        return;
      }
      
      setLoadingBid(true);
      try {
        const { data } = await supabase
          .from("bids")
          .select("amount, prediction")
          .eq("user_id", user.id)
          .eq("event_id", event.id)
          .maybeSingle();
        if (data) {
          setUserBid({ amount: Number(data.amount), prediction: data.prediction });
        } else {
          setUserBid(null);
        }
      } catch (error) {
        console.error("Error fetching user bid:", error);
      } finally {
        setLoadingBid(false);
      }
    };
    fetchUserBid();
  }, [user, event?.id, isOpen, prefetchedUserBid]);

  if (!event) return null;

  const isEventEnded = checkDeadlinePassed(event.deadline);
  const totalPool = event.yes_pool + event.no_pool;
  const yesPercent = totalPool > 0 ? (event.yes_pool / totalPool) * 100 : 50;
  const noPercent = totalPool > 0 ? (event.no_pool / totalPool) * 100 : 50;
  const statusDisplay = getEventStatusDisplay(event.status, event.deadline, locale.language as "en" | "pt");
  const eventUrl = `${window.location.origin}/event/${event.id}`;
  const shareText = `${event.title} - ${locale.language === "pt" ? "Faça sua previsão no Callyout!" : "Make your prediction on Callyout!"}`;

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(eventUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({
      title: locale.language === "pt" ? "Link copiado!" : "Link copied!",
      description: locale.language === "pt" ? "Compartilhe com seus amigos" : "Share it with your friends",
    });
    setShareOpen(false);
  };

  const handleShareTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(eventUrl)}`;
    window.open(url, "_blank");
    setShareOpen(false);
  };

  const handleShareWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${eventUrl}`)}`;
    window.open(url, "_blank");
    setShareOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || prediction === null) return;

    if (!checkRateLimit()) {
      toast({
        title: locale.language === "pt" ? "Muitas previsões" : "Too many predictions",
        description: locale.language === "pt" 
          ? "Aguarde um momento antes de fazer outra previsão" 
          : "Please wait a moment before placing another prediction",
        variant: "destructive",
      });
      return;
    }

    const bidAmount = parseFloat(amount);
    if (isNaN(bidAmount) || bidAmount <= 0) {
      toast({
        title: locale.language === "pt" ? "Valor inválido" : "Invalid amount",
        description: locale.language === "pt" ? "Digite um valor válido" : "Please enter a valid amount",
        variant: "destructive",
      });
      return;
    }

    if (profile && bidAmount > profile.duv_coins) {
      toast({
        title: locale.language === "pt" ? "Saldo insuficiente" : "Insufficient balance",
        description: locale.language === "pt" 
          ? "Você não tem CallyCoins suficientes" 
          : "You don't have enough CallyCoins",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { data: existingBid, error: existingBidError } = await supabase
        .from("bids")
        .select("id, amount")
        .eq("user_id", user.id)
        .eq("event_id", event.id)
        .eq("prediction", prediction)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (existingBidError) throw existingBidError;

      let isNewBid = true;

      if (existingBid) {
        const currentAmount = Number((existingBid as any).amount) || 0;
        const { error: updateError } = await supabase
          .from("bids")
          .update({ amount: currentAmount + bidAmount })
          .eq("id", existingBid.id);

        if (updateError) throw updateError;
        isNewBid = false;
      } else {
        const { error: bidError } = await supabase.from("bids").insert({
          user_id: user.id,
          event_id: event.id,
          amount: bidAmount,
          prediction: prediction,
        });

        if (bidError) throw bidError;
      }

      recordAction();

      if (profile && isNewBid) {
        await supabase
          .from("profiles")
          .update({ total_bids: profile.total_bids + 1 })
          .eq("user_id", user.id);
      }

      await refetchProfile();

      setAmount("");
      setPrediction(null);
      onBidPlaced();
      onClose();
    } catch (error: any) {
      toast({
        title: locale.language === "pt" ? "Erro" : "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const canBid = event.status === "OPEN" && !isEventEnded && user;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={onClose}
          >
            <div className="glass-card p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{languageFlags[event.language || "global"] || "🌍"}</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusDisplay.style}`}>
                    {statusDisplay.text}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Popover open={shareOpen} onOpenChange={setShareOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <ExternalLink size={16} />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-48 p-2 bg-card border-border" align="end">
                      <div className="space-y-1">
                        <button
                          onClick={handleCopyLink}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-muted transition-colors"
                        >
                          {copied ? <Check size={16} className="text-success" /> : <Copy size={16} />}
                          {locale.language === "pt" ? "Copiar link" : "Copy link"}
                        </button>
                        <button
                          onClick={handleShareTwitter}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-muted transition-colors"
                        >
                          <Twitter size={16} />
                          Twitter / X
                        </button>
                        <button
                          onClick={handleShareWhatsApp}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-muted transition-colors"
                        >
                          <MessageCircle size={16} />
                          WhatsApp
                        </button>
                      </div>
                    </PopoverContent>
                  </Popover>
                  <button
                    onClick={onClose}
                    className="p-2 rounded-lg hover:bg-muted transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Title */}
              <h2 className="text-xl font-bold text-foreground mb-2">
                {event.title}
              </h2>

              {/* Description */}
              <p className="text-sm text-muted-foreground mb-4">
                {event.description}
              </p>

              {/* Time Remaining */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                <Clock size={14} />
                <span>{getTimeRemaining(event.deadline, locale.language as "en" | "pt")}</span>
              </div>

              {/* Pool Distribution */}
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-success font-semibold">
                    {locale.language === "pt" ? "SIM" : "YES"} {yesPercent.toFixed(0)}%
                  </span>
                  <span className="text-destructive font-semibold">
                    {locale.language === "pt" ? "NÃO" : "NO"} {noPercent.toFixed(0)}%
                  </span>
                </div>
                <div className="h-3 bg-muted rounded-full overflow-hidden flex">
                  <div
                    className="h-full bg-gradient-to-r from-success to-success/70 transition-all duration-500"
                    style={{ width: `${yesPercent}%` }}
                  />
                  <div
                    className="h-full bg-gradient-to-r from-destructive/70 to-destructive transition-all duration-500"
                    style={{ width: `${noPercent}%` }}
                  />
                </div>
              </div>

              {/* Stats */}
              <div className="flex items-center justify-between text-sm text-muted-foreground mb-6 p-3 rounded-lg bg-muted/30">
                <div className="flex items-center gap-1">
                  <TrendingUp size={14} />
                  <span>{totalPool > 0 ? totalPool.toLocaleString() : "0"} {t("events.pool")}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Users size={14} />
                  <span>{event.total_bids} {t("events.bids")}</span>
                </div>
              </div>

              {/* User's Existing Bid */}
              {user && userBid && (
                <div className={`mb-4 p-4 rounded-lg border ${
                  userBid.prediction 
                    ? "bg-success/10 border-success/30" 
                    : "bg-destructive/10 border-destructive/30"
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {userBid.prediction ? (
                        <TrendingUp size={18} className="text-success" />
                      ) : (
                        <TrendingDown size={18} className="text-destructive" />
                      )}
                      <span className="text-sm font-medium">
                        {locale.language === "pt" ? "Sua previsão:" : "Your prediction:"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`font-bold ${userBid.prediction ? "text-success" : "text-destructive"}`}>
                        {userBid.prediction 
                          ? (locale.language === "pt" ? "SIM" : "YES") 
                          : (locale.language === "pt" ? "NÃO" : "NO")}
                      </span>
                      <span className="text-foreground font-semibold">
                        {userBid.amount.toLocaleString()} CallyCoins
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Resolved Outcome */}
              {event.status === "RESOLVED" && event.outcome !== null && (
                <div className={`mb-6 p-4 rounded-lg text-center font-bold ${
                  event.outcome ? "bg-success/20 text-success" : "bg-destructive/20 text-destructive"
                }`}>
                  {locale.language === "pt" ? "Resultado: " : "Outcome: "}
                  {event.outcome 
                    ? (locale.language === "pt" ? "SIM" : "YES") 
                    : (locale.language === "pt" ? "NÃO" : "NO")}
                </div>
              )}

              {/* Bid Section */}
              {canBid ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {profile && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                      <Coins size={18} className="text-primary" />
                      <span className="text-sm text-muted-foreground">
                        {locale.language === "pt" ? "Seu saldo:" : "Your balance:"}
                      </span>
                      <span className="font-bold text-foreground">
                        {profile.duv_coins.toLocaleString()} CallyCoins
                      </span>
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      {locale.language === "pt" ? "Sua previsão" : "Your prediction"}
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setPrediction(true)}
                        className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                          prediction === true
                            ? "border-success bg-success/20 text-success"
                            : "border-border hover:border-success/50 hover:bg-success/10"
                        }`}
                      >
                        <TrendingUp size={24} />
                        <span className="font-bold">{locale.language === "pt" ? "SIM" : "YES"}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setPrediction(false)}
                        className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                          prediction === false
                            ? "border-destructive bg-destructive/20 text-destructive"
                            : "border-border hover:border-destructive/50 hover:bg-destructive/10"
                        }`}
                      >
                        <TrendingDown size={24} />
                        <span className="font-bold">{locale.language === "pt" ? "NÃO" : "NO"}</span>
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground mb-1 block">
                      {locale.language === "pt" ? "Quantidade (CallyCoins)" : "Amount (CallyCoins)"}
                    </label>
                    <Input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="100"
                      min="1"
                      required
                    />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button type="button" variant="ghost" onClick={onClose} className="flex-1">
                      {locale.language === "pt" ? "Cancelar" : "Cancel"}
                    </Button>
                    <Button 
                      type="submit" 
                      variant={prediction === true ? "success" : prediction === false ? "danger" : "hero"}
                      className="flex-1" 
                      disabled={isLoading || prediction === null}
                    >
                      {isLoading 
                        ? (locale.language === "pt" ? "Prevendo..." : "Predicting...") 
                        : (locale.language === "pt" ? "Confirmar Previsão" : "Confirm Prediction")
                      }
                    </Button>
                  </div>
                </form>
              ) : !user ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      onClick={handleLoginRedirect}
                      className="h-14 text-lg font-bold bg-success hover:bg-success/90 text-success-foreground"
                    >
                      {locale.language === "pt" ? "SIM" : "YES"}
                    </Button>
                    <Button
                      onClick={handleLoginRedirect}
                      className="h-14 text-lg font-bold bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                    >
                      {locale.language === "pt" ? "NÃO" : "NO"}
                    </Button>
                  </div>
                  <p className="text-center text-sm text-muted-foreground">
                    {locale.language === "pt" 
                      ? "Faça login para fazer sua previsão" 
                      : "Sign in to make your prediction"}
                  </p>
                </div>
              ) : isEventEnded || event.status !== "OPEN" ? (
                <div className="text-center py-4 text-muted-foreground">
                  {event.status === "RESOLVED" 
                    ? (locale.language === "pt" ? "Evento finalizado" : "Event finished")
                    : (locale.language === "pt" ? "Previsões encerradas" : "Predictions closed")}
                </div>
              ) : null}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default EventDetailModal;
